// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { Swiper as SwiperInstance } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getMediaRatio, isVerticalMedia } from 'utils/mediaUtils';
import { isBlank } from 'utils/storeUtils';

import { useSoundEnabled } from '../../hooks/useSoundEnabled';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface IMedia {
  id: string;
  url: string;
  type: 'image' | 'video';
}

interface IGalleryProps {
  mediaItems: IMedia[];
  initialIndex?: number;
  onVerticalChange?: (isVertical: boolean) => void;
}

const slideType = {
  image: 'image',
  video: 'video',
} as const;

const Carousel: React.FC<IGalleryProps> = ({ mediaItems, initialIndex = 0, onVerticalChange }) => {
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const [mediaRatios, setMediaRatios] = useState<Record<string, number>>({});
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex);
  const { isSoundEnabled } = useSoundEnabled();

  const handleSlideChange = (swiper: SwiperInstance) => {
    const previousSlide = mediaItems[activeIndex];
    const currentSlide = mediaItems[swiper.activeIndex];
    setActiveIndex(swiper.activeIndex);

    if (previousSlide.type === slideType.video) {
      const video = videoRefs.current[previousSlide.id];
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    }

    if (currentSlide.type === slideType.video) {
      const video = videoRefs.current[currentSlide.id];
      if (video) {
        video.load();
        video.play();
      }
    }

    const ratio = mediaRatios[currentSlide.id];
    if (!isBlank(ratio)) {
      onVerticalChange?.(isVerticalMedia(ratio));
    }
  };

  const handleMediaLoad = (mediaId: string, e: React.SyntheticEvent<HTMLImageElement | HTMLVideoElement>) => {
    const element = e.target as HTMLImageElement | HTMLVideoElement;
    const ratio = getMediaRatio(element);

    setMediaRatios(prev => {
      const updatedRatios = { ...prev, [mediaId]: ratio };
      if (mediaItems[initialIndex]?.id === mediaId) {
        onVerticalChange?.(isVerticalMedia(ratio));
      }
      return updatedRatios;
    });
  };

  useEffect(() => {
    Object.values(videoRefs.current).forEach((video) => {
      if (video) {
        video.muted = !isSoundEnabled;
      }
    });
  }, [isSoundEnabled]);


  return (
    <StyledSwiper
      modules={[Navigation, Pagination]}
      navigation
      pagination={{ clickable: true }}
      spaceBetween={10}
      slidesPerView={1}
      initialSlide={initialIndex}
      onSlideChange={handleSlideChange}
    >
      {mediaItems.map((media, index) => {
        const isVertical = isVerticalMedia(mediaRatios[media.id] ?? 1);
        const isImageMedia = media.type === slideType.image;
        const isVideoMedia = media.type === slideType.video;

        return (
          <SwiperSlideStyled key={media.id}>
            <MediaWrapper $isVertical={isVertical}>
              {isImageMedia && (
                <StyledImage
                  src={media.url}
                  alt="Character photo"
                  $isVertical={isVertical}
                  onLoad={(e) => handleMediaLoad(media.id, e)}
                />
              )}
              {isVideoMedia && (
                <FullscreenVideo
                  ref={(videoElement) => (videoRefs.current[media.id] = videoElement)}
                  src={media.url}
                  $isVertical={isVertical}
                  loop
                  onClick={(e) => e.stopPropagation()}
                  onLoadedMetadata={(e) => handleMediaLoad(media.id, e)}
                  playsInline
                  muted={!isSoundEnabled}
                  autoPlay={index === activeIndex}
                />
              )}
            </MediaWrapper>
          </SwiperSlideStyled>
        );
      })}
    </StyledSwiper>
  );
};

export default Carousel;

const StyledSwiper = styled(Swiper)`
  width: 100%;
  max-width: 600px;

  .swiper-button-prev,
  .swiper-button-next {
    color: white;
  }

  .swiper-button-prev:after,
  .swiper-button-next:after {
    font-size: 24px;
    user-select: none;
  }
`;

const FullscreenVideo = styled.video<{ $isVertical?: boolean }>`
  max-width: 100%;
  max-height: 100%;
  height: ${props => props.$isVertical ? 'calc(85vh + 4px)' : 'auto'};
  width: auto;
  object-fit: contain;
  border-radius: 12px;
  display: block;
  margin: auto;
  user-select: none;

  @media (max-width: 768px) {
    height: ${props => props.$isVertical ? 'calc(auto + 4px)' : 'auto'};
    max-height: ${props => props.$isVertical ? 'calc(80vh + 4px)' : 'auto'};
    width: ${props => props.$isVertical ? 'auto' : '100%'};
    max-width: 100%;
  }
`;

const SwiperSlideStyled = styled(SwiperSlide)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  flex-direction: column;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
    min-height: 80vh;
  }
`;

const MediaWrapper = styled.div<{ $isVertical?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  max-width: 100vw;
  max-height: ${props => props.$isVertical ? '85vh' : '80vh'};
  overflow: hidden;
  
  @media (max-width: 720px) {
    max-height: ${props => props.$isVertical ? '80vh' : '70vh'};
    width: 100%;
    height: auto;
  }
`;

const StyledImage = styled.img<{ $isVertical?: boolean }>`
  max-width: 100%;
  max-height: 100%;
  height: ${props => props.$isVertical ? 'calc(85vh + 4px)' : 'auto'};
  width: auto;
  object-fit: contain;
  border-radius: 12px;
  display: block;
  margin: auto;
  user-select: none;
  
  @media (max-width: 720px) {
    height: ${props => props.$isVertical ? 'calc(auto + 4px)' : 'auto'};
    max-height: ${props => props.$isVertical ? 'calc(80vh + 4px)' : 'auto'};
    width: ${props => props.$isVertical ? 'auto' : '100%'};
    max-width: 100%;
  }
`;